import React, { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button, Skeleton } from "@mantine/core";
import {
  IconUsers,
  IconBuildingStore,
  IconCreditCard,
  IconShoppingBag,
  IconCoins,
  IconGift,
  IconStar,
  IconLink,
  IconChevronRight,
  IconClock,
  IconCheck,
} from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { useApp } from "../../../context/AppContext";
import { useClient } from "../../../context/ClientContext";
import { useProducts } from "../../../context/ProductsContext";
import { useOnboarding } from "../../../context/OnboardingContext";
import { useFeatureAccess } from "../../../context/FeatureAccessContext";
import useLoyaltyProgram from "../../../hooks/useLoyaltyProgram";
import CopyBookingLinkButton from "../../common/CopyBookingLinkButton";
import { generateBookingLink } from "../../../utils/bookingLinkUtils";
import { normalizeDepositConfigsResponse } from "../../../utils/depositConfigUtils";
import {
  OnboardingProgressResponse,
  ProgressiveStatus,
  isProgressiveOnboardingComplete,
  UpdateOnboardingProgressRequest,
} from "../types/onboarding";
import ProgressiveEmployeesWizard from "../progressive/ProgressiveEmployeesWizard";
import ProgressiveServicesWizard from "../progressive/ProgressiveServicesWizard";
import ProgressivePaymentsWizard from "../progressive/ProgressivePaymentsWizard";
import { Salon, Employee } from "../../../types/salon";
import CompletionModal from "./CompletionModal";
import MigrationSuccessModal from "./MigrationSuccessModal";
import styles from "./OnboardingDashboard.module.css";

interface OnboardingDashboardProps {
  progress: OnboardingProgressResponse;
  onRefresh?: () => void;
}

interface ChecklistCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: ProgressiveStatus | null;
  route?: string;
  onClick?: () => void;
}

function getStatusLabel(status: ProgressiveStatus | null): string {
  if (status === "complete") return "Complete";
  if (status === "inprogress") return "In progress";
  return "Not started";
}

function getStatusClassName(status: ProgressiveStatus | null): string {
  if (status === "complete") return styles.statusComplete;
  if (status === "inprogress") return styles.statusInProgress;
  return styles.statusNotStarted;
}

function StatusIcon({ status }: { status: ProgressiveStatus | null }) {
  if (status === "complete") {
    return <IconCheck size={14} color="#6aa580" />;
  }
  // Not started and in progress both use black icons
  return <IconClock size={14} color="#101820" />;
}

/** Placeholder card shown while loading data that determines which secondary cards to display (products, deposits). */
function SecondaryCardSkeleton() {
  return (
    <div className={styles.cardSkeleton} aria-hidden>
      <div className={styles.cardSkeletonHeader}>
        <Skeleton height={24} width={24} radius="sm" />
        <Skeleton height={18} width={18} radius="sm" />
      </div>
      <Skeleton
        height={20}
        width="70%"
        radius="sm"
        className={styles.cardSkeletonTitle}
      />
      <Skeleton height={14} width="100%" radius="sm" mt={6} />
      <Skeleton height={14} width="85%" radius="sm" mt={6} />
    </div>
  );
}

export default function OnboardingDashboard({
  progress,
  onRefresh,
}: OnboardingDashboardProps) {
  const navigate = useNavigate();
  const {
    businessInfo,
    refreshOnboardingProgress,
    services,
    employees,
    currentUser,
  } = useSalon() as {
    businessInfo?: Salon;
    refreshOnboardingProgress: (salonId?: string) => Promise<any>;
    services?: unknown[];
    employees?: unknown[];
    currentUser?: Employee;
  };
  const { allClients } = useClient();
  const { products, isLoading: productsLoading } = useProducts();
  const { depositConfigCount, setDepositConfigCount } = useOnboarding();
  const { getDepositConfigs, getTransactions } = useApp();
  const { hasFeature } = useFeatureAccess();
  // Loyalty card status: complete once the program is configured (same
  // app-root settings state the Loyalty page uses).
  const { isConfigured: loyaltyConfigured } = useLoyaltyProgram();

  // Modal states
  const [employeesWizardOpen, setEmployeesWizardOpen] = useState(false);
  const [servicesWizardOpen, setServicesWizardOpen] = useState(false);
  const [paymentsWizardOpen, setPaymentsWizardOpen] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);

  // Calculated state for onboarding progress
  // Updates when: 1) progress prop changes (GET response), 2) POST response completes, 3) optimistically on Next/Submit
  const [calculatedProgress, setCalculatedProgress] = useState(progress);

  // From existing context (already loaded when we're on onboarding)
  const hasProducts = (products?.length ?? 0) > 0;
  const hasClients = (allClients?.length ?? 0) > 0;
  const hasServices = (services?.length ?? 0) > 0;
  const hasEmployees = (employees?.length ?? 0) > 0;

  // Deposits: derived from OnboardingContext (set by fetch in effect below). Card is "complete" when at least one deposit config exists.
  const hasDeposits = (depositConfigCount ?? 0) > 0;
  const [hasAppointments, setHasAppointments] = useState(false);
  const [migrationDataLoaded, setMigrationDataLoaded] = useState(false);

  // Data migration form states
  const migrationStorageKey = businessInfo?.id
    ? `migration-submitted-${businessInfo.id}`
    : null;
  const [migrationSubmitting, setMigrationSubmitting] = useState(false);
  const [migrationSubmitted, setMigrationSubmitted] = useState(
    () =>
      !!migrationStorageKey &&
      localStorage.getItem(migrationStorageKey) === "true",
  );
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [migrationModalOpen, setMigrationModalOpen] = useState(false);

  // Update calculated state when progress prop changes (from GET onboarding response)
  useEffect(() => {
    if (progress) {
      setCalculatedProgress(progress);
    }
  }, [progress]);

  useEffect(() => {
    if (!businessInfo?.id) return;
    let cancelled = false;
    // Backend requires start_date and end_date. Use a wide range for "has any transactions" check.
    const end = new Date();
    const start = new Date(end);
    start.setFullYear(start.getFullYear() - 1);
    const toYMD = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const transactionQueryParams = `start_date=${toYMD(start)}&end_date=${toYMD(end)}&limit=1`;
    // Run both fetches independently so a failing getTransactions (e.g. 400 / non-JSON) does not block updating the deposit count
    Promise.allSettled([
      getDepositConfigs(businessInfo.id),
      getTransactions(businessInfo.id, transactionQueryParams),
    ]).then(([depositsResult, txResult]) => {
      if (cancelled) return;
      // Always process deposit configs so the tag updates even when transactions fails
      if (depositsResult.status === "fulfilled") {
        const depositConfigs = normalizeDepositConfigsResponse(
          depositsResult.value,
        );
        setDepositConfigCount(depositConfigs.length);
      } else {
        setDepositConfigCount(0);
      }
      if (txResult.status === "fulfilled") {
        const txList = txResult.value?.transactions ?? [];
        setHasAppointments(Array.isArray(txList) && txList.length > 0);
      }
      setMigrationDataLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [
    businessInfo?.id,
    getDepositConfigs,
    getTransactions,
    setDepositConfigCount,
  ]);

  // Determine if all progressive onboarding is complete (Growth mode)
  const isFullyComplete = isProgressiveOnboardingComplete(calculatedProgress);

  // Generate booking link from salon UUID using standardized utility
  const bookingLink = generateBookingLink(businessInfo?.id);

  // Check if all 3 primary tasks are complete to show the booking link section
  const hasCompletedAllPrimaryTasks =
    calculatedProgress.progressiveEmployees === "complete" &&
    calculatedProgress.progressiveServices === "complete" &&
    calculatedProgress.progressivePayments === "complete";

  const handleRefresh = useCallback(async () => {
    if (businessInfo?.id) {
      const newProgress = await refreshOnboardingProgress(businessInfo.id);
      if (newProgress) {
        setCalculatedProgress(newProgress);
      }
    }
    onRefresh?.();
  }, [businessInfo?.id, refreshOnboardingProgress, onRefresh]);

  // Handle wizard completion: fires POST async, updates calculated state optimistically, routes based on state
  // This function is passed to wizards and called when they click Next/Submit
  const handleWizardComplete = useCallback(
    async (
      section:
        | "progressive_employees"
        | "progressive_services"
        | "progressive_payments",
      postOnboardingProgress: (
        data: any,
        salonId?: string,
      ) => Promise<OnboardingProgressResponse>,
    ) => {
      // Step 1: Optimistically update calculated state using functional update to avoid stale closure
      // This ensures we always use the latest state when checking completion
      let updatedProgress: OnboardingProgressResponse | undefined;

      flushSync(() => {
        setCalculatedProgress((prev) => {
          const updated = {
            ...prev,
            [section]: "complete" as ProgressiveStatus,
          };
          updatedProgress = updated;
          return updated;
        });
      });

      // Type guard: updatedProgress is always defined after flushSync (synchronous)
      if (!updatedProgress) {
        return;
      }

      // Step 2: Determine routing based on calculated state
      // Show completion modal when 2 are complete AND 1 is in progress
      const statuses = [
        updatedProgress.progressiveEmployees,
        updatedProgress.progressiveServices,
        updatedProgress.progressivePayments,
      ];
      const completedCount = statuses.filter(
        (status) => status === "complete",
      ).length;
      const inProgressCount = statuses.filter(
        (status) => status === "inprogress",
      ).length;

      const shouldShowCompletionModal =
        completedCount === 2 && inProgressCount === 1;

      if (shouldShowCompletionModal) {
        // All complete: close all wizards, show completion modal
        flushSync(() => {
          setEmployeesWizardOpen(false);
          setServicesWizardOpen(false);
          setPaymentsWizardOpen(false);
          setCompletionModalOpen(true);
        });
      } else {
        // Not all complete: route to next wizard
        flushSync(() => {
          if (section === "progressive_employees") {
            setEmployeesWizardOpen(false);
            setServicesWizardOpen(true);
          } else if (section === "progressive_services") {
            setServicesWizardOpen(false);
            setPaymentsWizardOpen(true);
          } else if (section === "progressive_payments") {
            setPaymentsWizardOpen(false);
          }
        });
      }

      // Step 3: Fire POST request async (don't wait)
      // When POST completes, silently update calculated state with response if it differs
      // Don't refresh all data - this causes navigation/re-render issues and the optimistic update is sufficient
      // Only persist when the salon id is known: without the ?salonId= query
      // param the backend silently falls back to a staff-keyed row that can
      // belong to another location (a body salonId is ignored).
      if (!businessInfo?.id) {
        console.warn(
          "OnboardingDashboard: skipping progress save — no salon id resolved yet",
        );
        return;
      }
      postOnboardingProgress(
        {
          section,
          state: "complete",
        },
        businessInfo.id,
      )
        .then((response) => {
          // Silently update calculated state with POST response
          // This should match our optimistic update, but update if backend returned different state
          setCalculatedProgress(response);
        })
        .catch((error) => {
          // On error, revert optimistic update by refreshing from backend
          if (businessInfo?.id) {
            refreshOnboardingProgress(businessInfo.id).then((refreshed) => {
              if (refreshed) {
                setCalculatedProgress(refreshed);
              }
            });
          }
        });
    },
    [calculatedProgress, businessInfo?.id, refreshOnboardingProgress],
  );

  const handleCompletionClose = () => {
    setCompletionModalOpen(false);
    handleRefresh();
  };

  const handleMigrationSubmit = useCallback(async () => {
    if (migrationSubmitted || migrationSubmitting) return;
    setMigrationSubmitting(true);
    setMigrationError(null);

    const formData: Record<string, string> = {
      "form-name": "data-migration-interest",
      "bot-field": "",
      salon_name: businessInfo?.salon_name || "",
      legal_business_name: businessInfo?.legal_business_name || "",
      salon_phone_number:
        [businessInfo?.area_code, businessInfo?.phone_number]
          .filter(Boolean)
          .join("") || "",
      street_address_1: businessInfo?.street_address_1 || "",
      street_address_2: businessInfo?.street_address_2 || "",
      city: businessInfo?.city || "",
      state: businessInfo?.state || "",
      zip_code: businessInfo?.zip_code || "",
      timezone: businessInfo?.timezone || "",
      first_name: currentUser?.first_name || "",
      last_name: currentUser?.last_name || "",
      email: currentUser?.email || "",
      user_phone_number:
        [currentUser?.area_code, currentUser?.phone_number]
          .filter(Boolean)
          .join("") || "",
      role: currentUser?.role || "",
    };

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      if (!response.ok)
        throw new Error(`Form submission failed: ${response.status}`);
      setMigrationSubmitted(true);
      if (migrationStorageKey) {
        localStorage.setItem(migrationStorageKey, "true");
      }
      setMigrationModalOpen(true);
    } catch (error) {
      console.error("Migration form submission error:", error);
      setMigrationError(
        "Something went wrong. Please try again or email us at contact@getpagoda.com.",
      );
    } finally {
      setMigrationSubmitting(false);
    }
  }, [
    migrationSubmitted,
    migrationSubmitting,
    migrationStorageKey,
    businessInfo,
    currentUser,
  ]);

  // Handle switching between wizards from sidebar clicks
  const handleSwitchWizard = (
    wizard: "employees" | "services" | "payments",
  ) => {
    // Determine which wizard is currently open
    const currentlyOpen = employeesWizardOpen
      ? "employees"
      : servicesWizardOpen
        ? "services"
        : paymentsWizardOpen
          ? "payments"
          : null;

    // If clicking the same wizard that's already open, do nothing
    if (currentlyOpen === wizard) {
      return;
    }

    // Close currently open wizard and open target wizard simultaneously
    // React will batch these state updates, eliminating flicker
    switch (wizard) {
      case "employees":
        setEmployeesWizardOpen(true);
        setServicesWizardOpen(false);
        setPaymentsWizardOpen(false);
        break;
      case "services":
        setEmployeesWizardOpen(false);
        setServicesWizardOpen(true);
        setPaymentsWizardOpen(false);
        break;
      case "payments":
        setEmployeesWizardOpen(false);
        setServicesWizardOpen(false);
        setPaymentsWizardOpen(true);
        break;
    }
  };

  const primaryCards: ChecklistCard[] = [
    {
      id: "employees",
      title: "Add Employees",
      description: "Manage schedules, permissions, and staff calendars",
      icon: <IconUsers size={24} />,
      status: calculatedProgress.progressiveEmployees,
      onClick: () => setEmployeesWizardOpen(true),
    },
    {
      id: "services",
      title: "Add Services",
      description: "Add services, pricing, and durations",
      icon: <IconBuildingStore size={24} />,
      status: calculatedProgress.progressiveServices,
      onClick: () => setServicesWizardOpen(true),
    },
    {
      id: "payments",
      title: "Accept Payments",
      description:
        "Enable deposits, tips, and secure checkout to stay organized",
      icon: <IconCreditCard size={24} />,
      status: calculatedProgress.progressivePayments,
      onClick: () => setPaymentsWizardOpen(true),
    },
  ];

  const secondaryCards: ChecklistCard[] = [
    {
      id: "products",
      title: "Add Products",
      description:
        "Set up your product catalog. Email us at contact@getpagoda.com for help migrating clients",
      icon: <IconShoppingBag size={24} />,
      status: hasProducts ? "complete" : null,
      route: "/admin/products",
    },
    {
      id: "deposits",
      title: "Add Deposits",
      description: "Require deposits to reduce no-shows and cancellations.",
      icon: <IconCoins size={24} />,
      status: hasDeposits ? "complete" : null,
      route: "/admin/settings/deposits",
    },
    // Loyalty FE1 §1: entry card for the Loyalty page. While unconfigured it
    // deep-links into the setup wizard (?setup=1); once configured it shows
    // "complete" and opens the tab view. Only when the loyalty feature is
    // available. Note: the section's hide-when-done logic still keys on
    // Products + Deposits only.
    ...(hasFeature("loyalty")
      ? ([
          {
            id: "loyalty",
            title: "Set Up Loyalty Program",
            description:
              "Reward your clients for visits, purchases, and referrals.",
            icon: <IconGift size={24} />,
            status: loyaltyConfigured ? "complete" : null,
            route: loyaltyConfigured
              ? "/admin/loyalty/rewards"
              : "/admin/loyalty/rewards?setup=1",
          },
        ] as ChecklistCard[])
      : []),
  ];

  const showMigrationSection =
    migrationDataLoaded &&
    !(
      hasServices &&
      hasClients &&
      hasEmployees &&
      hasProducts &&
      hasAppointments
    );

  // Show skeleton cards while we're still loading data that determines which cards to show (products from context, deposits/transactions from fetch)
  const isSecondaryCardsLoading = !migrationDataLoaded || productsLoading;

  // When all secondary cards (Products + Deposits) are complete, hide the whole section so all cards disappear
  const allSecondaryCardsComplete = hasProducts && hasDeposits;

  const handleCardClick = (card: ChecklistCard) => {
    if (card.onClick) {
      card.onClick();
    } else if (card.route) {
      navigate(card.route);
    }
  };

  // Growth Dashboard (fully complete)
  if (isFullyComplete) {
    return (
      <>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.title}>Keep growing</h1>
            <p className={styles.subtitle}>
              Discover new features, tips, and updates to help you book more
              clients.
            </p>
          </div>

          {/* Booking Link Section - Always shown in Growth mode */}
          <div className={styles.bookingLinkBanner}>
            <h3 className={styles.bannerTitle}>
              Your booking page is live — don't keep it a secret!
            </h3>
          </div>

          {bookingLink && (
            <div className={styles.bookingLinkSection}>
              <div className={styles.bookingLinkContent}>
                <IconLink size={24} className={styles.bookingLinkIcon} />
                <div>
                  <div className={styles.bookingLinkTitle}>
                    Share Booking Link
                  </div>
                  <div className={styles.bookingLinkDescription}>
                    Your booking page is live and ready for clients. Share your
                    link to start accepting appointments.
                  </div>
                </div>
              </div>
              <CopyBookingLinkButton
                bookingLink={bookingLink}
                variant="button"
                size="md"
                color="teal"
              />
            </div>
          )}

          {/* Second success message: when all subtasks (products, deposits) are complete */}
          {!isSecondaryCardsLoading && allSecondaryCardsComplete && (
            <div className={styles.allDoneBanner}>
              <p className={styles.allDoneTitle}>
                Hooray — you've finished onboarding the Pagoda Platform.
              </p>
              <p className={styles.allDoneSubtitle}>
                For any additional questions or assistance, please reach out to
                our support team at{" "}
                <a
                  href="mailto:contact@getpagoda.com"
                  className={styles.migrationLink}
                >
                  contact@getpagoda.com
                </a>
              </p>
            </div>
          )}

          {/* Make the most out of Pagoda - hide section when all secondary cards are complete (so all cards disappear) */}
          {(isSecondaryCardsLoading || !allSecondaryCardsComplete) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Make the most out of Pagoda
              </h2>
              <div className={styles.cardsGrid}>
                {isSecondaryCardsLoading ? (
                  <>
                    <SecondaryCardSkeleton />
                    <SecondaryCardSkeleton />
                    <SecondaryCardSkeleton />
                  </>
                ) : (
                  secondaryCards.map((card) => (
                    <div
                      key={card.id}
                      className={styles.card}
                      onClick={() => handleCardClick(card)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleCardClick(card);
                        }
                      }}
                    >
                      <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}>{card.icon}</span>
                        <span
                          className={`${styles.statusBadge} ${getStatusClassName(card.status)}`}
                        >
                          <StatusIcon status={card.status} />
                          {getStatusLabel(card.status)}
                        </span>
                      </div>
                      <div className={styles.cardTitle}>
                        {card.title}
                        <IconChevronRight
                          size={18}
                          className={styles.chevron}
                        />
                      </div>
                      <p className={styles.cardDescription}>
                        {card.description}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Migration Section - hidden when salon has at least one of each: services, clients, employees, products, appointments */}
          {showMigrationSection && (
            <div className={styles.migrationSection}>
              <IconStar size={24} className={styles.migrationIcon} />
              <div className={styles.migrationContent}>
                <div className={styles.migrationTitle}>Migrate Your Data</div>
                <div className={styles.migrationDescription}>
                  Import clients, services, and appointments easily from any
                  platform. Let us help you get started!
                </div>
                {migrationError && (
                  <div className={styles.migrationError} role="alert">
                    {migrationError}
                  </div>
                )}
              </div>
              {!migrationSubmitted && (
                <Button
                  size="sm"
                  color="var(--brand-teal)"
                  variant="filled"
                  loading={migrationSubmitting}
                  disabled={!businessInfo}
                  onClick={handleMigrationSubmit}
                  className={styles.migrationButton}
                >
                  Learn More
                </Button>
              )}
            </div>
          )}
        </div>

        <CompletionModal
          opened={completionModalOpen}
          onClose={handleCompletionClose}
        />

        <MigrationSuccessModal
          opened={migrationModalOpen}
          onClose={() => setMigrationModalOpen(false)}
        />
      </>
    );
  }

  // Setup Dashboard (progressive onboarding incomplete)
  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>You're almost there</h1>
          <p className={styles.subtitle}>
            Here's your setup checklist — take it step by step at your own pace.
          </p>
        </div>

        {/* Booking Link Section - Show only when all 3 primary tasks are complete */}
        {hasCompletedAllPrimaryTasks && bookingLink && (
          <div className={styles.bookingLinkSection}>
            <div className={styles.bookingLinkContent}>
              <IconLink size={24} className={styles.bookingLinkIcon} />
              <div>
                <div className={styles.bookingLinkTitle}>
                  Your booking page is live — don't keep it a secret!
                </div>
                <div className={styles.bookingLinkDescription}>
                  Share Booking Link
                </div>
                <div className={styles.bookingLinkDescription}>
                  Your booking page is live and ready for clients. Share your
                  link to start accepting appointments.
                </div>
              </div>
            </div>
            <CopyBookingLinkButton
              bookingLink={bookingLink}
              variant="button"
              size="md"
              color="teal"
            />
          </div>
        )}

        {/* Primary Section - Finish your set up */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Finish your set up</h2>
          <p className={styles.sectionSubtitle}>
            Your last steps before getting up and running.
          </p>
          <div className={styles.cardsGrid}>
            {primaryCards.map((card) => (
              <div
                key={card.id}
                className={styles.card}
                onClick={() => handleCardClick(card)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCardClick(card);
                  }
                }}
              >
                <div className={styles.cardHeader}>
                  <span className={styles.cardIcon}>{card.icon}</span>
                  <span
                    className={`${styles.statusBadge} ${getStatusClassName(card.status)}`}
                  >
                    <StatusIcon status={card.status} />
                    {getStatusLabel(card.status)}
                  </span>
                </div>
                <div className={styles.cardTitle}>
                  {card.title}
                  <IconChevronRight size={18} className={styles.chevron} />
                </div>
                <p className={styles.cardDescription}>
                  {card.description
                    .split(/(contact@getpagoda\.com)/)
                    .map((part, idx) =>
                      part === "contact@getpagoda.com" ? (
                        <a
                          key={idx}
                          href="mailto:contact@getpagoda.com"
                          className={styles.migrationLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {part}
                        </a>
                      ) : (
                        part
                      ),
                    )}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Secondary Section - Make the most out of Pagoda - hide when all secondary cards are complete */}
        {(isSecondaryCardsLoading || !allSecondaryCardsComplete) && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Make the most out of Pagoda</h2>
            <div className={styles.cardsGrid}>
              {isSecondaryCardsLoading ? (
                <>
                  <SecondaryCardSkeleton />
                  <SecondaryCardSkeleton />
                </>
              ) : (
                secondaryCards.map((card) => (
                  <div
                    key={card.id}
                    className={styles.card}
                    onClick={() => handleCardClick(card)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleCardClick(card);
                      }
                    }}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.cardIcon}>{card.icon}</span>
                      <span
                        className={`${styles.statusBadge} ${getStatusClassName(card.status)}`}
                      >
                        <StatusIcon status={card.status} />
                        {getStatusLabel(card.status)}
                      </span>
                    </div>
                    <div className={styles.cardTitle}>
                      {card.title}
                      <IconChevronRight size={18} className={styles.chevron} />
                    </div>
                    <p className={styles.cardDescription}>
                      {card.description
                        .split(/(contact@getpagoda\.com)/)
                        .map((part, idx) =>
                          part === "contact@getpagoda.com" ? (
                            <a
                              key={idx}
                              href="mailto:contact@getpagoda.com"
                              className={styles.migrationLink}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {part}
                            </a>
                          ) : (
                            part
                          ),
                        )}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Migration Section - hidden when salon has at least one of each: services, clients, employees, products, appointments */}
        {showMigrationSection && (
          <div className={styles.migrationSection}>
            <IconStar size={24} className={styles.migrationIcon} />
            <div className={styles.migrationContent}>
              <div className={styles.migrationTitle}>Migrate Your Data</div>
              <div className={styles.migrationDescription}>
                Import clients, services, and appointments easily from any
                platform. Let us help you get started!
              </div>
              {migrationError && (
                <div className={styles.migrationError} role="alert">
                  {migrationError}
                </div>
              )}
            </div>
            {!migrationSubmitted && (
              <Button
                size="sm"
                color="var(--brand-teal)"
                variant="filled"
                loading={migrationSubmitting}
                onClick={handleMigrationSubmit}
                className={styles.migrationButton}
              >
                Learn More
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Progressive Wizards */}
      <ProgressiveEmployeesWizard
        opened={employeesWizardOpen}
        onClose={() => {
          setEmployeesWizardOpen(false);
          handleRefresh();
        }}
        progress={calculatedProgress}
        onComplete={(postOnboardingProgress) =>
          handleWizardComplete("progressive_employees", postOnboardingProgress)
        }
        onSwitchWizard={handleSwitchWizard}
      />

      <ProgressiveServicesWizard
        opened={servicesWizardOpen}
        onClose={() => {
          setServicesWizardOpen(false);
          handleRefresh();
        }}
        progress={calculatedProgress}
        onComplete={(postOnboardingProgress) =>
          handleWizardComplete("progressive_services", postOnboardingProgress)
        }
        onSwitchWizard={handleSwitchWizard}
      />

      <ProgressivePaymentsWizard
        opened={paymentsWizardOpen}
        onClose={() => {
          setPaymentsWizardOpen(false);
          handleRefresh();
        }}
        progress={calculatedProgress}
        onComplete={(postOnboardingProgress) =>
          handleWizardComplete("progressive_payments", postOnboardingProgress)
        }
        onSwitchWizard={handleSwitchWizard}
      />

      <CompletionModal
        opened={completionModalOpen}
        onClose={handleCompletionClose}
      />

      <MigrationSuccessModal
        opened={migrationModalOpen}
        onClose={() => setMigrationModalOpen(false)}
      />
    </>
  );
}
